import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { BiChat, BiChevronRight, BiGroup, BiListCheck, BiPlus } from 'react-icons/bi';
import { Link } from 'react-router-dom';

import {
  Avatar,
  Card,
  LoaderSpinner,
  SidePanel,
  Skeleton,
  StatusPill,
  type StatusTone,
} from '@/components';
import { ButtonOutlinedSmall } from '@/components/Button';
import CreateEditTaskSheet from '@/components/CreateSheet/CreateEditTaskSheet';
import { Checkbox } from '@/components/Inputs';
import { OPEN_TASK_STATUSES } from '@/components/TaskExpiringLabel/taskExpiration';
import { useAuth } from '@/store/auth/auth.store';
import { getVesselCrewChats } from '@/store/chats/chat.api';
import { useChats } from '@/store/chats/chats.store';
import {
  TASK_STATUSES,
  TaskParticipantType,
  type TaskResponse,
  TaskStatus,
  type TaskStatusType,
} from '@/store/tasks/tasks.dto';
import { useInvalidateTasks, useTasksListQuery } from '@/store/tasks/tasks.queries';
import { useTasks } from '@/store/tasks/tasks.store';
import { formatDate } from '@/utils/formatDate';
import { toast } from '@/utils/toast';
import CrewChat from '@pages/Crew/CrewChat';
import {
  CREW_PATH,
  crewMemberPath,
  type DisplayRow,
  getMemberName,
  getMemberRole,
  useCrewRoster,
} from '@pages/Crew/crewRoster';
import { tasksForAssigneeHref } from '@pages/TasksPage/use-task-filters';

import s from './CrewCard.module.scss';

const ALL_MATCHING = 99999;

const SKELETON_ROWS = 4;

const STATUS_LABEL = new Map(TASK_STATUSES.map(({ id, label }) => [id as string, label]));

const EMPTY_TASKS: TaskResponse[] = [];

const byDeadline = (a: TaskResponse, b: TaskResponse) =>
  (a.time_slot?.end ? 0 : 1) - (b.time_slot?.end ? 0 : 1) ||
  (a.time_slot?.end ?? '').localeCompare(b.time_slot?.end ?? '');

const STATUS_TONE: Record<string, StatusTone> = {
  [TaskStatus.PENDING]: 'neutral',
  [TaskStatus.IN_PROGRESS]: 'warning',
  [TaskStatus.ON_HOLD]: 'neutral',
  [TaskStatus.ON_APPROVAL]: 'info',
  [TaskStatus.COMPLETED]: 'success',
  [TaskStatus.CANCELED]: 'neutral',
  [TaskStatus.DENIED]: 'danger',
  [TaskStatus.FAILED]: 'danger',
};

const isOpen = (task: TaskResponse) => OPEN_TASK_STATUSES.includes(task.status);

const TasksPanel = ({ member, onClose }: { member: DisplayRow; onClose: () => void }) => {
  const updateTask = useTasks((state) => state.updateTask);
  const createTask = useTasks((state) => state.createTask);
  const invalidateTasks = useInvalidateTasks();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [visibleIds, setVisibleIds] = useState<string[]>([]);

  const query = useTasksListQuery({ responsible: member.id, limit: ALL_MATCHING }, 1);
  const tasks = query.data?.items ?? EMPTY_TASKS;
  const loading = query.isPending;

  useEffect(() => {
    if (loading) return;
    setVisibleIds(
      tasks
        .filter(isOpen)
        .sort(byDeadline)
        .map((task) => task.id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id, loading]);

  const name = getMemberName(member);
  const byId = new Map(tasks.map((task) => [task.id, task]));
  const ordered = visibleIds
    .map((id) => byId.get(id))
    .filter((task): task is TaskResponse => !!task);
  const openCount = tasks.filter(isOpen).length;

  const toggleDone = async (task: TaskResponse) => {
    const next = isOpen(task) ? TaskStatus.COMPLETED : TaskStatus.PENDING;
    try {
      await updateTask({ ...task, status: next });
      invalidateTasks();
      toast.success(next === TaskStatus.COMPLETED ? 'Task completed' : 'Task reopened');
    } catch {
      toast.error('Couldn’t update the task. Please try again.');
    }
  };

  return (
    <>
      <div className={s.panelRoot}>
        <header className={s.panelHeader}>
          <div className={s.panelMedia}>
            <Avatar
              size={40}
              src={member.avatar}
              firstName={member.firstName}
              lastName={member.lastName}
              alt=""
            />
          </div>
          <div className={s.panelIdentity}>
            <h2 className="headline-small">{name}</h2>
            <p className={s.panelSubtitle}>{getMemberRole(member)}</p>
            {!loading && ordered.length > 0 && (
              <div className={s.panelBadges}>
                <StatusPill
                  label={openCount === 0 ? 'All done' : `${openCount} open`}
                  tone={openCount === 0 ? 'success' : 'info'}
                />
              </div>
            )}
          </div>
        </header>

        <div className={s.panelSections}>
          <section className={s.panelSection}>
            <h3 className={s.panelSectionTitle}>Tasks</h3>
            <div className={s.panelSectionBody}>
              {loading ? (
                <div className={s.taskLoading}>
                  <LoaderSpinner fullHeight={false} />
                </div>
              ) : ordered.length === 0 ? (
                <p className={s.taskEmpty}>
                  <BiListCheck size={32} aria-hidden="true" />
                  {tasks.length > 0
                    ? `${member.firstName} is all caught up.`
                    : `Please assign ${member.firstName} a task to get started.`}
                </p>
              ) : (
                <ul className={s.taskList}>
                  {ordered.map((task) => (
                    <li key={task.id} className={s.taskCard} data-done={!isOpen(task) || undefined}>
                      <div className={s.taskHead}>
                        <Checkbox
                          className={s.taskCheck}
                          id={`crew-task-${task.id}`}
                          label={task.title}
                          checked={!isOpen(task)}
                          onChange={() => void toggleDone(task)}
                        />
                        <StatusPill
                          label={STATUS_LABEL.get(task.status) ?? task.status}
                          tone={STATUS_TONE[task.status] ?? 'neutral'}
                        />
                      </div>
                      <span className={s.taskMeta}>
                        {task.time_slot?.end
                          ? `Due ${formatDate(task.time_slot.end)}`
                          : 'No due date'}
                        {' · '}
                        {task.priority} priority
                        {task.type ? ` · ${task.type}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        <footer className={s.panelActions}>
          <div className={s.panelFooter}>
            <ButtonOutlinedSmall className={s.newTaskButton} onClick={() => setIsSheetOpen(true)}>
              <BiPlus aria-hidden="true" /> New task
            </ButtonOutlinedSmall>

            <Link
              className={s.panelFooterLink}
              to={tasksForAssigneeHref(member.id)}
              onClick={onClose}
            >
              See all tasks for {member.firstName}
              <BiChevronRight aria-hidden="true" />
            </Link>
          </div>
        </footer>
      </div>

      <CreateEditTaskSheet
        title="Add Task"
        open={isSheetOpen}
        assigneeId={member.id}
        assigneeName={name}
        showAssignee
        onClose={() => setIsSheetOpen(false)}
        onSubmitTask={async (payload) => {
          const created = await createTask({
            ...payload,
            responsible: {
              person_id: member.id,
              type: TaskParticipantType.CREW,
              name,
            },
          });
          invalidateTasks();
          if (created?.id) setVisibleIds((prev) => [...prev, created.id]);
        }}
      />
    </>
  );
};

/**
 * `fillHeight` is for a host that sizes the card itself (the dashboard cell):
 * the roster then takes whatever height it is given instead of stopping at the
 * skeleton's four rows.
 */
const CrewCard = ({
  className,
  headDivider,
  fillHeight,
}: {
  className?: string;
  headDivider?: boolean;
  fillHeight?: boolean;
}) => {
  const getOrCreateOneToOneRoom = useChats((state) => state.getOrCreateOneToOneRoom);
  const currentUserId = useAuth((state) => state.session?.id);
  const { crewRows, inviteRows, isCrewListLoading, isInvitesLoading, isPeopleLoading } =
    useCrewRoster();

  const [tasksFor, setTasksFor] = useState<DisplayRow | null>(null);
  const [openingChatFor, setOpeningChatFor] = useState<string | null>(null);
  const [isOpeningCrewChat, setIsOpeningCrewChat] = useState(false);
  const [chat, setChat] = useState<{
    roomId: string;
    member?: DisplayRow;
  } | null>(null);

  const roster = useMemo(() => [...crewRows, ...inviteRows], [crewRows, inviteRows]);

  const tasksQuery = useTasksListQuery(
    {
      status: OPEN_TASK_STATUSES as TaskStatusType[],
      limit: ALL_MATCHING,
    },
    1,
  );

  const openByPerson = useMemo(() => {
    const map = new Map<string, TaskResponse[]>();
    for (const task of tasksQuery.data?.items ?? []) {
      if (!task.responsible_id) continue;
      const list = map.get(task.responsible_id);
      if (list) list.push(task);
      else map.set(task.responsible_id, [task]);
    }
    for (const list of map.values()) list.sort(byDeadline);
    return map;
  }, [tasksQuery.data]);

  const loading = isCrewListLoading || isInvitesLoading || isPeopleLoading;
  const countsReady = !tasksQuery.isPending;
  const rowsClass = clsx(s.rows, !fillHeight && s.rowsCapped);

  /**
   * Both chat buttons open the conversation in a panel over this card, the
   * same `SidePanel` + `CrewChat` pairing a project uses on
   * /manage/projects/:id. Manage is a screen you scan and act from, so a
   * conversation opens beside the roster you opened it from.
   */
  const openCrewChat = async () => {
    if (isOpeningCrewChat) return;
    setIsOpeningCrewChat(true);
    try {
      const { data } = await getVesselCrewChats();
      if (data?.crew_chat_id) setChat({ roomId: data.crew_chat_id });
      else toast.error('Couldn’t open the crew chat. Please try again.');
    } catch {
      toast.error('Couldn’t open the crew chat. Please try again.');
    } finally {
      setIsOpeningCrewChat(false);
    }
  };

  const openMemberChat = async (member: DisplayRow) => {
    if (openingChatFor) return;
    setOpeningChatFor(member.id);
    try {
      const room = await getOrCreateOneToOneRoom(member.id);
      if (room?.id) setChat({ roomId: room.id, member });
      else toast.error('Couldn’t open the conversation. Please try again.');
    } catch {
      toast.error('Couldn’t open the conversation. Please try again.');
    } finally {
      setOpeningChatFor(null);
    }
  };

  return (
    <Card as="section" ariaLabel="Crew" className={clsx(s.card, className)}>
      <div className={clsx(s.head, headDivider && s.headDivided)}>
        <BiGroup className={s.headIcon} aria-hidden="true" />
        <h2 className="headline-small">Crew</h2>
        {loading ? (
          <Skeleton variant="text" width={24} height={14} />
        ) : (
          <span className={s.count}>({roster.length})</span>
        )}
        <Link className={s.headLink} to={CREW_PATH}>
          See all crew <BiChevronRight aria-hidden="true" />
        </Link>
      </div>

      {loading ? (
        <>
          <Skeleton variant="block" height={42} className={s.allCrewBone} />

          <ul className={rowsClass} aria-busy="true">
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <li key={i} className={s.row}>
                <span className={s.skeletonPerson}>
                  <Skeleton variant="circle" width={32} height={32} />
                  <span className={s.who}>
                    <Skeleton variant="text" width={64} height={12} />
                    <Skeleton variant="text" width={120} height={14} />
                  </span>
                </span>
                <Skeleton variant="block" width={60} height={22} className={s.pillBone} />
              </li>
            ))}
          </ul>
        </>
      ) : roster.length === 0 ? (
        <p className={s.empty}>Please add crew to get started.</p>
      ) : (
        <>
          {crewRows.length > 0 && (
            <button
              type="button"
              className={s.allCrew}
              disabled={isOpeningCrewChat}
              onClick={() => void openCrewChat()}
            >
              <span className={s.allCrewIcon} aria-hidden="true">
                <BiChat />
              </span>
              Chat with All Crew
              <BiChevronRight className={s.allCrewChevron} aria-hidden="true" />
            </button>
          )}

          <ul className={rowsClass} aria-label={`Crew roster, ${roster.length} people`}>
            {roster.map((member) => {
              const name = getMemberName(member);
              const open = openByPerson.get(member.id) ?? [];

              return (
                <li key={member.id} className={s.row}>
                  <Link
                    className={s.person}
                    to={crewMemberPath(member.id)}
                    aria-label={`${name}, ${getMemberRole(member)}`}
                  >
                    <Avatar
                      size={32}
                      src={member.avatar}
                      firstName={member.firstName}
                      lastName={member.lastName}
                      alt=""
                      className={s.face}
                    />
                    <span className={s.who}>
                      <span className={s.role}>{getMemberRole(member)}</span>
                      <span className={s.name}>{name}</span>
                    </span>
                  </Link>

                  {member.isInvite && (
                    <StatusPill label="Invited" tone="info" className={s.rowStatus} />
                  )}

                  {!member.isInvite && (
                    <>
                      <button
                        type="button"
                        className={clsx(
                          s.action,
                          countsReady && open.length > 0 && s.actionCounted,
                        )}
                        aria-haspopup="dialog"
                        aria-expanded={tasksFor?.id === member.id}
                        aria-label={
                          !countsReady
                            ? `Open tasks for ${name}, still loading`
                            : open.length === 0
                              ? `Open tasks for ${name}: none`
                              : `Open tasks for ${name}: ${open.length}`
                        }
                        onClick={() =>
                          setTasksFor((current) => (current?.id === member.id ? null : member))
                        }
                      >
                        <BiListCheck aria-hidden="true" />
                        {countsReady && open.length > 0 && (
                          <span className={s.actionCount} aria-hidden="true">
                            {open.length}
                          </span>
                        )}
                      </button>

                      {/* Your own row keeps Tasks but loses Message: there is
                          no conversation with yourself to open. */}
                      {member.id !== currentUserId && (
                        <button
                          type="button"
                          className={s.action}
                          disabled={openingChatFor === member.id}
                          aria-label={`Message ${name}`}
                          onClick={() => void openMemberChat(member)}
                        >
                          <BiChat aria-hidden="true" />
                        </button>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <SidePanel open={Boolean(chat)} onClose={() => setChat(null)}>
        {chat &&
          (chat.member ? (
            <CrewChat
              roomId={chat.roomId}
              firstName={chat.member.firstName}
              lastName={chat.member.lastName}
              avatar={chat.member.avatar}
              role={getMemberRole(chat.member)}
              onBack={() => setChat(null)}
            />
          ) : (
            <CrewChat
              roomId={chat.roomId}
              title="All crew"
              role="Crew conversation"
              chatType="group"
              onBack={() => setChat(null)}
            />
          ))}
      </SidePanel>

      <SidePanel open={Boolean(tasksFor)} onClose={() => setTasksFor(null)}>
        {tasksFor && <TasksPanel member={tasksFor} onClose={() => setTasksFor(null)} />}
      </SidePanel>
    </Card>
  );
};

export default CrewCard;
